import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Button, Checkbox, DatePickerPopover, FormGroup, Label } from "@xola/ui-kit";
import clsx from "clsx";
import { formatDate, isBefore, now, toLocalDate } from "common";
import dayjs from "dayjs";
import React from "react";
import { BaseSchedule, TimeRange, WeekdayCopy } from "types";
import { useSeller } from "../../hooks/useAccount";
import { getAllWeekdays } from "../../models/Weekday";
import { TimeRangeSelector } from "../Common/TimeRangeSelector";
import { ManageActionButton } from "../Dashboard/Roster/ManageActionButton";

export interface BaseScheduleEditorProps {
    values: BaseSchedule;
    onChange: (values: BaseSchedule) => void;
    inModal?: boolean;
    errors?: { timeRanges?: Array<{ startTime?: string; endTime?: string }> };
    disablePastDays?: boolean;
    /** Defines styles for showing weekday input. defaults to "checkbox" */
    weekdaysType?: WeekdayCheckboxProps["type"];
}

// TODO: The `inModal` prop should ideally be in ui-kit's Popover.
export const BaseScheduleEditor = ({
    values,
    onChange,
    inModal = false,
    errors,
    disablePastDays = false,
    weekdaysType = "checkbox",
}: BaseScheduleEditorProps) => {
    const seller = useSeller();

    const [animationParent] = useAutoAnimate<HTMLDivElement>();
    const popoverProps = inModal ? { appendTo: document.body, zIndex: 40, placement: "auto" } : undefined;
    const { timeRanges = [] } = values;

    const getDateWithSellerTimezone = (date: Date) => dayjs(date).tz(seller.timezoneName, true);

    const handleStartDateChange = (start: Date) => {
        const startDate = getDateWithSellerTimezone(start).startOf("day");
        onChange({ ...values, start: startDate });
    };

    const handleEndDateChange = (end: Date | null) => {
        const endDate = end ? getDateWithSellerTimezone(end).endOf("day") : null;
        onChange({ ...values, end: endDate });
    };

    const isWeekdayChecked = (dayIndex: number) => {
        return values.days?.includes(dayIndex);
    };

    const handleWeekdayChange = (dayIndex: number) => {
        const days = isWeekdayChecked(dayIndex)
            ? values.days?.filter((index) => index !== dayIndex)
            : [...(values.days ?? []), dayIndex];

        onChange({ ...values, days: days && days.length > 0 ? days : getAllWeekdays() });
    };

    const handleNoEndChange = () => {
        const start = values.start ?? new Date(); // For `formatDate` you need to ensure start is not null
        const endDate = values.end ? null : dayjs.max(now(), dayjs(formatDate(start)))!.toDate();
        handleEndDateChange(endDate);
    };

    const handleDeleteTimeRange = (deletedIndex: number) => {
        const { timeRanges = [] } = values;
        onChange({ ...values, timeRanges: timeRanges.filter((timeRange, index) => index !== deletedIndex) });
    };

    const handleAddTimeRangeClick = () => {
        const timeRange = {};
        onChange({ ...values, timeRanges: [...timeRanges, timeRange as TimeRange] });
    };

    const handleTimeRangeChange = (updatedTimeRange: Partial<TimeRange>, updatedIndex: number) => {
        const updatedTimeRanges = timeRanges.map((timeRange, index) => {
            return index === updatedIndex ? updatedTimeRange : timeRange;
        });

        onChange({ ...values, timeRanges: updatedTimeRanges as TimeRange[] });
    };

    return (
        <div>
            <div className="flex items-center space-x-4">
                <FormGroup className="flex-1">
                    <Label>Start Date</Label>

                    <DatePickerPopover
                        value={dayjs(formatDate(values.start ?? new Date())).toDate()}
                        popoverProps={popoverProps}
                        disabledDays={[
                            {
                                after: values.end ? dayjs(values.end).toDate() : undefined,
                                before: disablePastDays ? now().toDate() : undefined,
                            },
                        ]}
                        onChange={handleStartDateChange}
                    />
                </FormGroup>

                <FormGroup className="flex-1">
                    <Label>End Date</Label>

                    <DatePickerPopover
                        value={values.end ? toLocalDate(dayjs(values.end)).toDate() ?? null : null}
                        popoverProps={popoverProps}
                        disabledDays={(date: Date) => isBefore(date, values.start ?? new Date(), "day")}
                        onChange={handleEndDateChange}
                    />
                </FormGroup>

                <Checkbox
                    className="flex-shrink-0"
                    label="No end"
                    data-testid="base-schedule-editor-no-end-checkbox"
                    checked={!values.end}
                    onChange={handleNoEndChange}
                />
            </div>

            <FormGroup>
                <Label>Days of Week</Label>

                <div className={clsx("flex items-center", weekdaysType === "checkbox" ? "space-x-4" : "space-x-1")}>
                    {getAllWeekdays().map((weekday) => (
                        <WeekdayCheckbox
                            key={weekday}
                            type={weekdaysType}
                            label={WeekdayCopy[weekday]}
                            checked={!!isWeekdayChecked(weekday)}
                            onChange={() => handleWeekdayChange(weekday)}
                        />
                    ))}
                </div>
            </FormGroup>

            <div ref={animationParent}>
                {timeRanges.map((timeRange, index) => (
                    <TimeRangeSelector
                        // The index is the only identifier for the time ranges,
                        // so we need to disable the following rule:
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        showLabel={index === 0}
                        value={timeRange}
                        errors={errors?.timeRanges?.[index]}
                        onChange={(value) => handleTimeRangeChange(value, index)}
                        onDelete={() => handleDeleteTimeRange(index)}
                    />
                ))}
            </div>

            <ManageActionButton text="Add Time Range" onClick={handleAddTimeRangeClick} />
        </div>
    );
};

interface WeekdayCheckboxProps {
    type: "checkbox" | "button";
    label: string;
    checked: boolean;
    onChange: () => void;
}

const WeekdayCheckbox = ({ type, label, checked, onChange }: WeekdayCheckboxProps) => {
    if (type === "button") {
        return (
            <Button
                className={clsx(
                    checked && "bg-primary-lighter hover:bg-primary-lighter",
                    "w-14 text-black focus:ring-0",
                )}
                color={checked ? "primary" : "secondary"}
                variant="outline"
                size="small"
                onClick={onChange}
            >
                {label}
            </Button>
        );
    }

    return <Checkbox label={label} checked={checked} onChange={onChange} />;
};
